/**
 * @engine/logger
 * 
 * Game engine logging library with DevTools integration
 * 
 * Features:
 * - Multiple log levels (Trace, Debug, Info, Warn, Error, Fatal)
 * - Category-based filtering (Render, Physics, Audio, etc.)
 * - DevTools integration (groups, tables, styling, timers)
 * - Rate limiting for duplicate messages
 * - Custom log handlers
 * - Per-category configuration
 * 
 * @example
 * ```typescript
 * import { Logger, Log, LogLevel } from '@engine/logger';
 * 
 * // Use pre-configured category loggers
 * Log.Render.info('Frame rendered', { fps: 60 });
 * Log.Physics.debug('Bodies:', 100);
 * 
 * // Create custom category logger
 * const log = Logger.create('MySystem');
 * log.info('System initialized');
 * 
 * // Configure logging
 * Logger.setLevel(LogLevel.Debug);
 * Logger.disableCategory('Physics');
 * Logger.setCategoryLevel('Render', LogLevel.Warn);
 * 
 * // DevTools features
 * log.table('Players', players);
 * log.time('Update');
 * // ... do work
 * log.timeEnd('Update');
 * 
 * log.grouped('Complex Operation', () => {
 *   log.debug('Step 1');
 *   log.debug('Step 2');
 * });
 * ```
 */

// Core exports
export { Logger, Log } from './Logger';
export { LogLevel } from './types';
export type {
  LogCategory,
  BuiltinLogCategory,
  LoggerConfig,
  CategoryConfig,
  LogEntry,
  LogHandler,
  TimerInfo,
  AssertionFailure,
  AssertionHandler,
  LogGroupScope,
  LogTimerScope,
} from './types';
export {
  DEFAULT_CATEGORY_COLORS,
  LOG_LEVEL_COLORS,
  LOG_LEVEL_NAMES,
} from './types';

// DevTools detection
export {
  devToolsDetector,
  supportsConsoleStyling,
  supportsConsoleGrouping,
  supportsConsoleTable,
  supportsConsoleTiming,
  getDevToolsFeatures,
} from './DevToolsDetector';
export type { DevToolsState } from './DevToolsDetector';

// Re-export Logger as default
export { Logger as default } from './Logger';
